'use client';

import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

interface MetricCardProps {
  icon: string;
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  description: string;
  showChange?: boolean;
  forceUpArrow?: boolean;
}

const MetricCard = ({ icon, title, value, change, changeType, description, showChange = true, forceUpArrow }: MetricCardProps) => (
  <div className="bg-card rounded-lg p-6 shadow-organic-sm border border-border hover:shadow-organic transition-organic duration-300">
    <div className="flex items-start justify-between mb-4">
      <div className="p-3 bg-primary/10 rounded-lg">
        <Icon name={icon as any} size={28} variant="solid" className="text-primary" />
      </div>
      {showChange && (
        <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${
          changeType === 'positive' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          <Icon
            name={forceUpArrow || changeType === 'positive' ? 'ArrowUpIcon' : 'ArrowDownIcon'}
            size={14}
            variant="solid"
            className="inline mr-1"
          />
          {change}
        </span>
      )}
    </div>
    <h3 className="text-sm font-body font-medium text-muted-foreground mb-2">{title}</h3>
    <p className="text-3xl font-headline font-bold text-foreground mb-2">{value}</p>
    <p className="text-sm text-muted-foreground font-body">{description}</p>
  </div>
);

const ImpactMetrics = () => {
  const metrics: MetricCardProps[] = [
    {
      icon: 'ShieldCheckIcon',
      title: 'African Rhinos Poached in 2024',
      value: '516*',
      change: '+23%',
      changeType: 'positive',
      description: '*The Latest Data Is From 2024'
    },
    {
      icon: 'CurrencyDollarIcon',
      title: 'Funds Raised ',
      value: '$3,500+',
      change: '+18%',
      changeType: 'positive',
      description: 'Community fundraising campaigns',
      showChange: false,
    },
    {
      icon: 'ExclamationTriangleIcon',
      title: '',
      value: '175',
      change: '+98.6%',
      changeType: 'negative',
      forceUpArrow: true,
      description: 'Kruger National Park Poaching Incidents, 2025'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Community Members',
      value: '1,237',
      change: '+42%',
      changeType: 'positive',
      description: 'Engaged in conservation'
    }
  ];

  const monthlyData = [
    { month: 'Jan', saved: 145, incidents: 18 },
    { month: 'Feb', saved: 132, incidents: 15 },
    { month: 'Mar', saved: 168, incidents: 12 },
    { month: 'Apr', saved: 154, incidents: 14 },
    { month: 'May', saved: 189, incidents: 11 },
    { month: 'Jun', saved: 176, incidents: 9 },
    { month: 'Jul', saved: 198, incidents: 8 },
    { month: 'Aug', saved: 187, incidents: 10 },
    { month: 'Sep', saved: 165, incidents: 13 },
    { month: 'Oct', saved: 142, incidents: 11 },
    { month: 'Nov', saved: 156, incidents: 12 },
    { month: 'Dec', saved: 135, incidents: 10 }
  ];

  const populationTrend = [
    { year: '2020', population: 23630 },
    { year: '2021', population: 22578 },
    { year: '2022', population: 22137 },
    { year: '2023', population: 22137 },
    { year: '2024', population: 23885 },
    { year: '2025', population: 22540 }
  ];

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Conservation Impact Metrics
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Real-time data showing the tangible results of our collective conservation efforts across Africa.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {metrics.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        <div className="grid lg:grid-cols-1 gap-8">
          <div className="bg-card rounded-lg p-6 shadow-organic border border-border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-headline font-bold text-foreground mb-1">
                  Population Growth Trend
                </h3>
                <p className="text-sm text-muted-foreground font-body">
                  African rhino population (2020-2026)
                </p>
              </div>
              <Icon name="ChartPieIcon" size={24} variant="solid" className="text-secondary" />
            </div>
            <div className="w-full h-80" aria-label="Population Growth Trend Line Chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={populationTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 69, 19, 0.1)" />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(139, 69, 19, 0.2)' }}
                  />
                  <YAxis
                    tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(139, 69, 19, 0.2)' }}
                    domain={[23000, 28000]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '0.375rem',
                      fontSize: '14px'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="population"
                    stroke="var(--color-primary)"
                    strokeWidth={3}
                    dot={{ fill: 'var(--color-primary)', r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center mt-4">
              <div className="px-4 py-2 bg-error/10 rounded-full">
                <span className="text-sm font-body font-medium text-error">
                  <Icon name="ArrowTrendingDownIcon" size={16} variant="solid" className="inline mr-1" />
                  -4.61% decrease since 2020
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactMetrics;